import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import DishImage from "@/components/DishImage";

const R = 33; // katori ring radius (% from center)
const RICE = "https://images.pexels.com/photos/8923092/pexels-photo-8923092.jpeg?auto=compress&cs=tinysrgb&w=400";
const BHAKRI = "https://images.pexels.com/photos/11160565/pexels-photo-11160565.jpeg?auto=compress&cs=tinysrgb&w=400";

// Realistic top-down thali. Dishes sit in katoris seated into a steel plate
// (flat, grounded, non-overlapping). Hover highlights a dish with its name.
// `view` = "plate" | "list".
export default function PlateComposer({ dishes, onRemove, view = "plate", lang = "en", center = "rice" }) {
  const count = dishes.length;

  // equal katori size, guaranteed non-overlapping (chord between neighbours)
  const size = count <= 1 ? 27 : Math.min(27, 2 * R * Math.sin(Math.PI / count) * 0.86);

  const posFor = (idx) => {
    const n = Math.max(count, 1);
    const a = (idx / n) * Math.PI * 2 - Math.PI / 2;
    return { x: 50 + R * Math.cos(a), y: 50 + R * Math.sin(a) };
  };

  /* ---------------------------------------------------- LIST VIEW */
  if (view === "list") {
    const grouped = dishes.reduce((acc, d) => {
      (acc[d.category] = acc[d.category] || []).push(d);
      return acc;
    }, {});
    return (
      <div className="w-full" data-testid="thali-list">
        {count === 0 ? (
          <div className="py-16 text-center text-[#B08D4F]/70">
            <p className="serif text-2xl">Your Thali is empty</p>
            <p className="text-xs mt-1 tracking-wide">Add dishes to see them listed here</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
            {Object.keys(grouped).map((cat) => (
              <div key={cat} data-testid={`list-group-${cat.replace(/[^a-zA-Z]/g, "-")}`}>
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="label-tag text-[#B08D4F] text-[0.6rem]">{cat}</span>
                  <span className="text-[10px] text-[#b0a894]">{grouped[cat].length}</span>
                  <span className="flex-1 h-px bg-[#E5DCC9]" />
                </div>
                <div className="space-y-1.5">
                  {grouped[cat].map((d) => (
                    <motion.div
                      key={d.id}
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="group flex items-center gap-3 p-2 rounded-sm border border-[#E5DCC9] bg-white hover:border-[#C9A66B] transition-colors"
                      data-testid={`list-dish-${d.id}`}
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-[#E5DCC9]">
                        <DishImage src={d.image} alt={d.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="serif text-base text-[#1A1A1A] leading-tight truncate">{d.name}</h4>
                        {lang === "mr" && d.desc_mr ? <p className="text-[11px] text-[#5b554b] mt-0.5 leading-snug line-clamp-1">{d.desc_mr}</p> : null}
                      </div>
                      {onRemove && (
                        <button onClick={() => onRemove(d.id)} data-testid={`list-remove-${d.id}`}
                          className="text-[#b9b1a2] hover:text-red-500 transition-colors p-1 opacity-0 group-hover:opacity-100" aria-label={`Remove ${d.name}`}>
                          <X size={14} />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------- PLATE VIEW */
  return (
    <div className="relative w-full aspect-square max-w-[520px] mx-auto select-none" data-testid="plate-composer">
      {/* steel thali */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: "radial-gradient(circle at 42% 36%, #fbfcfd 0%, #e6e9ec 32%, #c6ccd3 60%, #a8afb8 82%, #ccd1d7 100%)",
          boxShadow:
            "0 44px 60px -26px rgba(0,0,0,0.5), inset 0 0 0 8px rgba(255,255,255,0.55), inset 0 0 0 12px #C9A66B, inset 0 0 0 15px rgba(176,141,79,0.55), inset 0 14px 44px rgba(255,255,255,0.45), inset 0 -22px 55px rgba(0,0,0,0.13)",
        }}
      />
      {/* brushed metal sheen */}
      <div
        className="absolute inset-[16px] rounded-full pointer-events-none opacity-40 mix-blend-overlay"
        style={{ background: "repeating-conic-gradient(from 0deg at 50% 50%, rgba(255,255,255,0.10) 0deg 3deg, rgba(0,0,0,0.06) 3deg 6deg)" }}
      />
      {/* centre — chosen base (rice or bhakri) */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: "30%" }} data-testid="thali-center">
        <div className="relative aspect-square rounded-full overflow-hidden" style={{ border: "4px solid #eef1f4", boxShadow: "inset 0 7px 12px rgba(0,0,0,0.45), 0 3px 8px rgba(0,0,0,0.25)" }}>
          <img src={center === "bhakri" ? BHAKRI : RICE} alt={center === "bhakri" ? "Bhakri" : "Steamed rice"} className="w-full h-full object-cover" />
        </div>
      </div>

      {count === 0 && (
        <div className="absolute left-1/2 bottom-[9%] -translate-x-1/2 text-center pointer-events-none">
          <p className="text-xs tracking-wide text-[#5b554b] bg-white/70 px-3 py-1 rounded-sm">{lang === "mr" ? "ताटात पदार्थ जोडा" : "Add dishes to plate them"}</p>
        </div>
      )}

      <AnimatePresence>
        {dishes.map((d, idx) => {
          const p = posFor(idx);
          return (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.4 }}
              transition={{ type: "spring", stiffness: 300, damping: 20, delay: idx * 0.02 }}
              className="absolute group"
              style={{ left: `${p.x}%`, top: `${p.y}%`, width: `${size}%`, transform: "translate(-50%, -50%)" }}
              data-testid={`plate-bowl-${d.id}`}
            >
              {/* name chip on hover */}
              <div className="absolute left-1/2 -translate-x-1/2 -top-7 z-20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <span className="whitespace-nowrap bg-[#1A1A1A] text-[#C9A66B] text-[10px] font-semibold px-2.5 py-1 rounded-sm shadow-lg">
                  {d.name}
                </span>
              </div>

              {/* katori seated in plate */}
              <div
                className="relative rounded-full transition-transform duration-300 group-hover:scale-[1.07]"
                style={{ padding: "4px", background: "linear-gradient(145deg,#f4f6f8,#c4cad1 46%,#8f969f)", boxShadow: "0 3px 5px rgba(0,0,0,0.28)" }}
              >
                <div
                  className="rounded-full overflow-hidden aspect-square"
                  style={{ boxShadow: "inset 0 7px 11px rgba(0,0,0,0.5), inset 0 -3px 6px rgba(255,255,255,0.18)", outline: "0px solid rgba(201,166,107,0)" }}
                >
                  <DishImage src={d.image} alt={d.name} className="w-full h-full object-cover" />
                </div>
                {/* gold highlight ring on hover */}
                <div className="absolute inset-0 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" style={{ boxShadow: "0 0 0 2px #C9A66B, 0 0 16px rgba(201,166,107,0.6)" }} />
                {onRemove && (
                  <button
                    onClick={() => onRemove(d.id)}
                    className="absolute -top-1 -right-1 z-20 bg-[#1A1A1A]/90 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`plate-remove-${d.id}`}
                    aria-label={`Remove ${d.name}`}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
